/**
 * Parser CSV minimale per l'import skill dalla pagina admin.
 *
 * Il CSV è interpretato lato client e trasformato in righe `{ name }` da
 * inviare a `POST /api/skills/import`. Il nome è la chiave: il backend salta i
 * nomi già presenti. L'associazione alla competenza è scelta dalla UI (non
 * dal CSV) e vale per tutte le skill importate.
 *
 * Formato atteso:
 *  - prima riga di intestazione con i nomi delle colonne (case‑insensitive);
 *  - colonna `name` obbligatoria (alias: `nome`, `skill`);
 *  - colonne opzionali `level` (alias: `livello`) e `weight` (alias: `peso`),
 *    riferite all'associazione con la competenza scelta nella UI;
 *  - colonna opzionale `description` (alias: `descrizione`), attributo della
 *    skill applicato indipendentemente dalla competenza scelta;
 *  - colonna opzionale `type` (alias: `tipo`), attributo della skill con
 *    valori `knowledge`/`skill`/`technology` (i valori non riconosciuti sono
 *    ignorati);
 *  - separatore virgola `,`; supporto dei campi tra virgolette doppie con
 *    escape a virgolette raddoppiate (`"a""b"` → `a"b`).
 *
 * Se non è presente un'intestazione riconosciuta, il file viene interpretato
 * **posizionalmente**: `name` nella prima colonna e, se presenti, `level` nella
 * seconda e `weight` nella terza. In assenza di ulteriori colonne ogni riga è
 * un semplice nome.
 *
 * Riusa lo stesso stile dei parser utenti/competenze: non è un parser CSV
 * completo ma copre gli export tipici da fogli di calcolo.
 */
import {
  MAX_SKILL_WEIGHT,
  MIN_SKILL_WEIGHT,
  isLivello,
  isSkillType,
  type ImportSkillRow,
  type Livello,
  type SkillType,
} from '../shared/dto';

/** Errore sollevato quando il contenuto CSV non è utilizzabile. */
export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

/** Alias di intestazione accettati per la colonna nome (normalizzati lowercase). */
const NAME_ALIASES: readonly string[] = ['name', 'nome', 'skill'];

/** Alias di intestazione accettati per la colonna livello richiesto. */
const LEVEL_ALIASES: readonly string[] = ['level', 'livello'];

/** Alias di intestazione accettati per la colonna peso. */
const WEIGHT_ALIASES: readonly string[] = ['weight', 'peso'];

/** Alias di intestazione accettati per la colonna descrizione. */
const DESCRIPTION_ALIASES: readonly string[] = ['description', 'descrizione'];

/** Alias di intestazione accettati per la colonna tipo. */
const TYPE_ALIASES: readonly string[] = ['type', 'tipo'];

/**
 * Costruisce una riga di import a partire dai campi grezzi `name`/`type`/
 * `description`/`level`/`weight`. Il `name` vuoto restituisce `null` (riga da
 * scartare). `description` è inclusa solo se non vuota; `type` solo se è uno
 * dei valori ammessi (`knowledge`/`skill`/`technology`); `level` e `weight`
 * solo se validi (Livello in scala; peso intero in `MIN..MAX`): i valori
 * mancanti o fuori scala vengono semplicemente ignorati, così un CSV con celle
 * vuote resta importabile.
 */
function buildRow(
  rawName: string | undefined,
  rawLevel: string | undefined,
  rawWeight: string | undefined,
  rawDescription: string | undefined,
  rawType: string | undefined,
): ImportSkillRow | null {
  const name = (rawName ?? '').trim();
  if (name === '') return null;

  const description = (rawDescription ?? '').trim();
  const type = (rawType ?? '').trim().toLowerCase();
  const level = (rawLevel ?? '').trim().toLowerCase();
  const weightText = (rawWeight ?? '').trim();
  const weight = weightText === '' ? Number.NaN : Number(weightText);

  const hasDescription = description !== '';
  const hasType = isSkillType(type);
  const hasLevel = isLivello(level);
  const hasWeight =
    Number.isInteger(weight) &&
    weight >= MIN_SKILL_WEIGHT &&
    weight <= MAX_SKILL_WEIGHT;

  return {
    name,
    ...(hasDescription ? { description } : {}),
    ...(hasType ? { type: type as SkillType } : {}),
    ...(hasLevel ? { level: level as Livello } : {}),
    ...(hasWeight ? { weight } : {}),
  };
}

/** Scompone una singola riga CSV in campi, gestendo le virgolette doppie. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1; // salta la virgoletta di escape
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Interpreta il testo CSV in un elenco di righe skill pronte per l'import.
 *
 * Se l'intestazione non contiene una colonna `name` riconosciuta, si assume
 * che il file abbia un'unica colonna di nomi (senza intestazione): in tal caso
 * ogni riga è trattata come un nome di skill.
 *
 * @throws {CsvParseError} se il file è vuoto o non contiene nomi validi.
 */
export function parseSkillsCsv(text: string): ImportSkillRow[] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    throw new CsvParseError('Il file CSV è vuoto.');
  }

  const header = splitCsvLine(lines[0]);
  const findCol = (aliases: readonly string[]): number =>
    header.findIndex((h) => aliases.includes(h.trim().toLowerCase()));
  const nameIdx = findCol(NAME_ALIASES);

  const rows: ImportSkillRow[] = [];

  if (nameIdx === -1) {
    // Nessuna intestazione riconosciuta: interpretazione posizionale
    // `name,level,weight`. Se le righe hanno una sola colonna sono solo nomi;
    // le colonne 2ª/3ª, se presenti, sono livello e peso. La prima riga è dato.
    for (const line of lines) {
      const fields = splitCsvLine(line);
      const row = buildRow(fields[0], fields[1], fields[2], undefined, undefined);
      if (row !== null) rows.push(row);
    }
  } else {
    const levelIdx = findCol(LEVEL_ALIASES);
    const weightIdx = findCol(WEIGHT_ALIASES);
    const descriptionIdx = findCol(DESCRIPTION_ALIASES);
    const typeIdx = findCol(TYPE_ALIASES);
    for (let i = 1; i < lines.length; i += 1) {
      const fields = splitCsvLine(lines[i]);
      const row = buildRow(
        fields[nameIdx],
        levelIdx === -1 ? undefined : fields[levelIdx],
        weightIdx === -1 ? undefined : fields[weightIdx],
        descriptionIdx === -1 ? undefined : fields[descriptionIdx],
        typeIdx === -1 ? undefined : fields[typeIdx],
      );
      if (row !== null) rows.push(row);
    }
  }

  if (rows.length === 0) {
    throw new CsvParseError('Nessuna riga skill valida nel CSV.');
  }

  return rows;
}
